import { useCallback } from "react";
import { ListResponse, useFetch } from "../infrastructure/useFetch";
import { AlbumFiltersParams } from "../pages/music/AlbumsFilters";
import { DraftFiltersParams, DraftSearchType } from "../pages/music/DraftFilters";
import { Config } from "../config";
import { TrackFiltersParams } from "../pages/music/TracksFilters";
import { PartialNullable } from "../types";
import { StatisticPeriod, StatisticPlatform } from "../pages/statisctics/StatisticsPage";
import { StatisticsAlbumsFiltersParams } from "../pages/statisctics/views/StatistisAlbumsFilters";
import { StatisticsTopsFiltersParams } from "../pages/statisctics/views/StatisticsTopsFilters";
import { TrackServiceType } from "../pages/album/wizard/Tracks/financialModels/TrackFinancialModels";
import { CreateArtistPayload } from "../components/Modals/CreateArtistModal";
import exp from "node:constants";

export enum RegionSettings {
  ALL = "ALL",
  RU = "RU",
}

export type LocalizedString = {
  [RegionSettings.ALL]: string | null;
  [RegionSettings.RU]: string | null;
  vk_url?: string | null;
  front_key?: string | null;
};

export type CountryMetaData = {
  title: LocalizedString;
  subtitle: LocalizedString;
  artists: {
    main: LocalizedString[];
    featured: LocalizedString[];
  };
  start_point: string | null; //add
};

export type TrackReleaseSet = {
  id: number | string;
  status: string;
  external_id: string | null;
  code_grid: string | null;
  code_isrc: string | null;
  display_title: string | null;
  display_artist_name: string | null;
  track_number: number;
  country_meta: CountryMetaData;
  album_part: number | string;
  contributors: Contributors | null;
  lyric: string | null;
  composer: string | null;
  text: string | null;

  file: string | null;
  file_name: string | null;
  file_path_b64?: string | null;
  file_meta: TrackFileMetaData;
  focus: boolean;
  right_author_external_id: string | number;
  right_author_percentage: number | string | null;
  genres: Array<string | number>;
  is_explicit: boolean;
  services?: TrackFinancialModel;
};

export type TrackFileMetaData = {
  name: string;
  size: number;
  duration: number;
};

export enum Country {
  ALL = "ALL",
  RU = "RU",
  UA = "UA",
  BY = "BY",
  AZ = "AZ",
  AM = "AM",
  KZ = "KZ",
  KG = "KG",
  MD = "MD",
  TJ = "TJ",
  TM = "TM",
  UZ = "UZ",
  GE = "GE",
}

export type TrackServiceModel = {
  countries: Partial<Record<Partial<keyof typeof Country>, { start: string }>>;
  story?: boolean;
  clip?: boolean;
};

export type TrackFinancialModel = Record<
  Partial<keyof typeof TrackServiceType>,
  TrackServiceModel
>;

export type Album = {
  id: number;
  image_url_template: string | null;
  country_meta: CountryMetaData;

  contractor: Contractor;
  genres?: Array<number | string>;
  code_icpn?: string | null;
  external_id?: string | null;
  code_grid?: string | null;
  is_explicit?: boolean;
  trackrelease_set?: Array<TrackReleaseSet>;

  content_type: string | null;

  upload_date: string | null;
  pub_date: string | null;
  release_date: string | null;
  original_release_date: string | null;
  tracks_count: number | null;
};

export type DraftAlbum = {
  id: number;
  contractor_id: number | null;
  status: string;
  upload_date: string | null;
  pub_date: string | null;
  release_date: string | null;
  original_release_date: string | null;
  imprt: number | null;
  duration: number | null;
  code_icpn: string | null;
  external_id: string | null;
  code_grid: string | null;
  trackrelease_set?: Array<TrackReleaseSet>;

  genres: Array<number | string>;
  country_meta: CountryMetaData;
  is_explicit: boolean;
  display_title: string | null;
  image: File | string | null;
  image_url_template: string | null;
  content_type: "audio" | "video" | string | null;
  tracks_count: number | null;
};

export type ChangeDraftAlbum = {
  code_grid: string | null;
  code_icpn: string | null;
  content_type: string | null;
  contractor_id: number | null;
  country_meta: CountryMetaData;
  is_explicit: boolean;
  original_release_date: string | null;
  release_date: string | null;
  genres: Array<string | number>;
};

export type Genre = {
  id: number;
  title: string;
  alias: string;
};

export type TrackAlbum = {
  code_icpn: string | null;
} & Album;

export type StatisticsTrackItem = {
  album_id: number;
  album_image_url: string;
  album_title: string;
  artist_name: string;
  is_mine: boolean;
  track_id: number;
  track_title: string;
  streams: number | null;
};

export type StatisticsAlbumItem = {
  album_id: number;
  album_image_url: string;
  album_title: string;
  artist_name: string;
};

export type StatisticsAlbumInfo = {
  artist_name: string;
  album_title: string;
  streams: StatisticsAlbumTrackInfo[];
};

export type StatisticsTrackInfo = {
  artist_name: string;
  track_title: string;
  releases: StatisticsTrackReleaseInfo[];
};

export type StatisticsDataItem = {
  amount: number | string;
  date: string;
};

export type StatisticsAlbumTrackInfo = {
  track_id: number | string;
  track_title: string;
};

export type StatisticsTrackReleaseInfo = {
  release_id: number | string;
  track_title: string;
  album_id: number | string;
  album_title: string;
};

export type MultiTrackTableItem = {
  id: number;
  external_id: string | null;
  code_isrc: string | null;
  track_number: number;
  Lyrics: string | null;
  Composer: string | null;
  album: number;
  is_explicit: boolean;
  focus: boolean;
  genres: number[];
  album_part: number;
  country_meta: CountryMetaData;
  file: number | string | null;
  file_path_b64: string | null;
  services: TrackFinancialModel;
};

export type TrackRelease = {
  id: number;
  track_id: number | null;
  status: string;
  external_id: string;
  code_isrc: string | null;
  album_part: string | null; //add
  track_number: string | null;
  p_line: string | null;
  c_line: string | null;
  code_grid: string | null;
  contractor: number | null; //add
  is_explicit: boolean;
  release_date: string | null;
  original_release_date: string | null;
  display_title: string;
  display_artist_name: string;
  subtitle: string;
  content_type: string;
  focus: boolean;

  file_name: string | null;
  file_path_b64?: string | null;

  contributors: Contributors;
  genres: Array<number | string>;
  country_meta: CountryMetaData;
  album: TrackAlbum | null;
  right_author_external_id?: string;
  right_author_percentage?: number | string;
};

export type Contributors = {
  Composer: string | null;
  Lyric: string | null;
}; //add

export type CmsPaymentUnit = {
  id: number;
  country_code: string;
  start: string;
};

export type CmsPayment = {
  download: Array<CmsPaymentUnit>;
  gift: Array<CmsPaymentUnit>;
  subscription: Array<CmsPaymentUnit>;
  advertisement: Array<CmsPaymentUnit>;
  story: {
    paid: boolean;
    free: boolean;
  };
  clip: {
    paid: boolean;
    free: boolean;
  };
};

export type Contractor = {
  id: number;
  status: string;
  type: string;
  title: string;
  contract: string;
  description: string;
  contact: string;
  url: string;
  allow_gift: boolean;
  allow_download: boolean;
  priority_export: boolean;
  related_right_royalty: number;
  author_right_royalty: number;
};

export type ContractorParams = {
  contractor_id: number;
}

export type DashboardUpcomingReleaseParams = {
  full_list: boolean;
  week_id: number;
} & ContractorParams

export type DashboardUpcomingRelease = {
  release_date: string;
  code_icpn: string;
  cover_url: string;
  full_title: string;
  number_of_tracks: number;
}

export type DashboardWeek = {
  week_id: number;
  week_title: string;
}

export type DashboardUpcomingReleaseResponse = {
  available_weeks: DashboardWeek[];
  current_week_id: DashboardWeek["week_id"];
  data: DashboardUpcomingRelease[]
}

export type DashboardTopEntityResponse<T> = {
  date_start: string;
  date_end: string;
  data: T[];
}

export type TopTrack = {
  title: string;
  artist_name: string;
  subtitle: string;
  image_link: string;
  release_link: string;
  streams: number;
}

export type TopArtist = {
  artist_name: string
  streams: number;
}

export function useMusicApi() {
  const client = useFetch();

  const getContractors = useCallback(async () => {
    const result = await client.get<ListResponse<Contractor>>(
      Config.BASE_API_URL + `/contractor/?format=json&limit=1000`,
      {
        withCredentials: false
      }
    );

    return result.data;
  }, []);

  const getAlbum = useCallback(async (id: string) => {
    const result = await client.get<Album>(
      Config.BASE_API_URL + `/album/${id}/?format=json`,
      {
        withCredentials: false
      }
    );

    return result.data;
  }, []);

  const getStatisticsTops = useCallback(
    async (
      platform: StatisticPlatform,
      period: StatisticPeriod
    ): Promise<ListResponse<StatisticsTrackItem>> => {
      const result = await client.get<StatisticsTrackItem[]>(
        Config.BASE_STATS_API_URL + `/top/${platform}/${period}/`,
        {
          withCredentials: false
        }
      );

      return {
        count: 100,
        results: result.data,
        previous: "",
        next: ""
      };
    },
    []
  );

  const getStatisticsForAlbum = useCallback(
    async (albumId: number | string) => {
      const result = await client.get<StatisticsAlbumInfo>(
        Config.BASE_STATS_API_URL + `/album_detail/${albumId}/`,
        {
          withCredentials: false
        }
      );

      return (result.data as any).data as StatisticsAlbumInfo;
    },
    []
  );

  const getStatisticsForTrack = useCallback(
    async (trackId: number | string) => {
      const result = await client.get<StatisticsTrackInfo>(
        Config.BASE_STATS_API_URL + `/track_detail/${trackId}/`,
        {
          withCredentials: false
        }
      );

      return (result.data as any).data as StatisticsTrackInfo;
    },
    []
  );

  const getStatisticsDataItems = useCallback(
    async (
      trackId: number | string,
      release_id: number | string | null,
      platform: StatisticPlatform = StatisticPlatform.VK_BOOM,
      period: StatisticPeriod = StatisticPeriod.LAST_WEEK
    ) => {
      const url =
        release_id != null
          ? `/release_streams/${release_id}/${platform}/${period}`
          : `/track_streams/${trackId}/${platform}/${period}`;
      const result = await client.get<StatisticsDataItem[]>(
        Config.BASE_STATS_API_URL + url,
        {
          withCredentials: false
        }
      );

      return (result.data as any).data as StatisticsDataItem[];
    },
    []
  );

  const getStatisticsAlbums = useCallback(
    async (
      params: StatisticsAlbumsFiltersParams
    ): Promise<ListResponse<StatisticsAlbumItem>> => {
      const result = await client.get<{
        count: number;
        data: { albums: StatisticsAlbumItem[] };
      }>(Config.BASE_STATS_API_URL + `/albums/`, {
        params: {
          ...params,
          rows_to_show: 50
        },
        withCredentials: false
      });

      return {
        count: result.data.count,
        results: result.data.data.albums,
        previous: "",
        next: ""
      };
    },
    []
  );

  const getStatisticsTracks = useCallback(
    async (
      params: StatisticsTopsFiltersParams
    ): Promise<ListResponse<StatisticsTrackItem>> => {
      const result = await client.get<{
        count: number;
        data: StatisticsTrackItem[];
      }>(
        `${Config.BASE_STATS_API_URL}/tracks/${params.platform}/${params.period}/`,
        {
          params: {
            ...params
          },
          withCredentials: false
        }
      );

      return {
        count: result.data.count,
        results: result.data.data,
        previous: "",
        next: ""
      };
    },
    []
  );

  const startDownloadStatisticsReportWithoutDate =
    useCallback(async (): Promise<string> => {
      const result = await client.get<string>(
        `${Config.BASE_STATS_API_URL}/start_unloading_report/`,
        { withCredentials: false }
      );

      return (result.data as any).data;
    }, []);

  const getCanAccessStatisticsReport =
    useCallback(async (): Promise<boolean> => {
      const result = await client.get<string>(
        `${Config.BASE_STATS_API_URL}/access_to_download_report/`,
        { withCredentials: false }
      );

      console.log(result);
      return (result.data as any).data;
    }, []);

  const startDownloadStatisticsReport = useCallback(
    async (date: Date): Promise<string> => {
      const result = await client.get<string>(
        `${Config.BASE_STATS_API_URL}/stats_report/${date.getFullYear()}/${
          date.getMonth() + 1
        }/${date.getDate()}/`,
        {
          withCredentials: false
        }
      );

      return (result.data as any).data;
    },
    []
  );

  const checkPrepareDownloadStatisticsReport = useCallback(
    async (taskId: string): Promise<string> => {
      const result = await client.get<string>(
        `${Config.BASE_STATS_API_URL}/check_report_status/${taskId}/`,
        {
          withCredentials: false
        }
      );

      return (result.data as any).data;
    },
    []
  );

  const downloadStatisticsReport = useCallback(
    async (downloadPostfix: string): Promise<any> => {
      const result = await client.get<any>(
        `${Config.BASE_STATS_API_URL}/download_report/${downloadPostfix}/`,
        {
          withCredentials: false,
          responseType: "blob"
        }
      );

      return result.data;
    },
    []
  );

  const getAlbums = useCallback(async (params: AlbumFiltersParams) => {
    const result = await client.get<ListResponse<Album>>(
      Config.BASE_API_URL + `/album/`,
      {
        params: {
          ...params,
          format: "json"
        },
        withCredentials: false
      }
    );

    return result.data;
  }, []);

  const getTracks = useCallback(async (params: TrackFiltersParams) => {
    const result = await client.get<ListResponse<TrackRelease>>(
      Config.BASE_API_URL + "/track_release/",
      {
        params: {
          ...params,
          format: "json"
        },
        withCredentials: false
      }
    );

    return result.data;
  }, []);

  const getTrack = useCallback(async (id: string) => {
    const result = await client.get<TrackRelease>(
      Config.BASE_API_URL + `/track_release/${id}/?format=json`,
      {
        withCredentials: false
      }
    );

    return result.data;
  }, []);

  const getDraftTrack = useCallback(async (id: string) => {
    const result = await client.get<TrackRelease>(
      Config.BASE_API_URL + `/draft_track_release/${id}/?format=json`,
      {
        withCredentials: false
      }
    );

    return result.data;
  }, []);

  const getCmsPayment = useCallback(async (id: string) => {
    const result = await client.get<CmsPayment>(
      Config.BASE_API_URL + `/cms_payment_models/${id}/?format=json`,
      {
        withCredentials: false
      }
    );

    return result.data;
  }, []);

  const getDrafts = useCallback(async (params: DraftFiltersParams) => {
    let url = "draft_album";
    if (params != null) {
      url =
        params?.type === DraftSearchType.EDIT
          ? "draft_album"
          : "album_in_publish";
    }

    const result = await client.get<ListResponse<DraftAlbum>>(
      Config.BASE_API_URL + `/${url}/`,
      {
        params: {
          ...params,
          format: "json"
        },
        withCredentials: false
      }
    );

    return result.data;
  }, []);

  const getAlbumDraft = useCallback(async (draftAlbumId: string | number) => {
    const result = await client.get<DraftAlbum>(
      Config.BASE_API_URL + `/draft_album/${draftAlbumId}?format=json`,
      {
        withCredentials: false
      }
    );

    return result.data;
  }, []);

  const getGenres = useCallback(async () => {
    const result = await client.get<ListResponse<Genre>>(
      Config.BASE_API_URL + "/genre/?format=json&limit=1000",
      {
        withCredentials: false
      }
    );

    return result.data;
  }, []);

  const patchUpdateAlbumDraft = useCallback(
    async (draftAlbumId: string | number, data: ChangeDraftAlbum) => {

      const result = await client.patch<ChangeDraftAlbum>(
        Config.BASE_API_URL + `/draft_album/${draftAlbumId}/?format=json`,
        { ...data },
        {
          withCredentials: false
        }
      );
      return result.data;
    },
    []
  );

  const postCreateAlbumDraft = useCallback(async (data: ChangeDraftAlbum) => {
    const result = await client.post<ChangeDraftAlbum>(
      Config.BASE_API_URL + `/draft_album/`,
      { ...data },
      {
        params: {
          format: "json"
        },
        withCredentials: false
      }
    );
    return result.data;
  }, []);

  const postUpdateMultiTrack = useCallback(
    async (
      draftAlbumId: string | number,
      data: PartialNullable<MultiTrackTableItem>[]
    ) => {
      const result = await client.post<any>(
        Config.BASE_API_URL +
        `/draft_album/${draftAlbumId}/multi_draft_track_release/`,
        data,
        {
          withCredentials: false
        }
      );
      return result.data;
    },
    []
  );

  const postMoveAlbumToDraft = useCallback(
    async (albumId: string | number, contractorId: string | number) => {
      const result = await client.post<DraftAlbum>(
        Config.BASE_API_URL + `/clone_cms_album_to_draft`,
        {
          contractor_id: contractorId,
          album_id: albumId
        },
        {
          withCredentials: false
        }
      );
      return result.data;
    },
    []
  );

  const postTearDownAlbum = useCallback(async (albumId: string | number) => {
    const result = await client.put(
      Config.BASE_API_URL + `/teardown_cms_album_and_rights`,
      [albumId],
      {
        withCredentials: false
      }
    );
    return result.data;
  }, []);

  const getAlbumURL = useCallback(async (id: string) => {
    const result = await client.get<{ shortlink?: string | null }>(
      Config.BASE_API_URL + `/shortlink/${id}/?format=json`,
      {
        withCredentials: false
      }
    );

    return result.data;
  }, []);

  const postPublishAlbumDraft = useCallback(async (id: string | number) => {
    const result = await client.post<DraftAlbum>(
      Config.BASE_API_URL + `/processing_albums`,
      [id],
      {
        withCredentials: false
      }
    );
    return result.data;
  }, []);

  const deleteDraftAlbum = useCallback(async (id: string | number) => {
    await client.deleteRequest(Config.BASE_API_URL + `/draft_album/${id}`, {
      withCredentials: false
    });
  }, []);

  const postUploadFile = useCallback(async (file: Blob | null) => {
    let formData = new FormData();
    formData.append("file", file ?? "");

    const result = await client.post<DraftAlbum>(
      Config.BASE_API_URL + `/audio_upload_fine_uploader/upload/`,
      formData ?? {},
      {
        headers: {
          "Content-Type": "multipart/form-data"
        },
        withCredentials: false
      }
    );
    return result.data;
  }, []);

  const patchUpdateTrack = useCallback(
    async (draftTrackId: string | number, data: TrackRelease) => {
      const result = await client.patch<TrackRelease>(
        Config.BASE_API_URL +
        `/draft_track_release/${draftTrackId}?format=json`,
        { ...data },
        {
          withCredentials: false
        }
      );
      return result.data;
    },
    []
  );

  const putUpdateReleaseAlbumsDate = useCallback(async (ids: Array<any>) => {
    const result = await client.put(
      Config.BASE_API_URL + `/allow_now_for_cms_album`,
      ids,
      {
        withCredentials: false
      }
    );
    return result.data;
  }, []);

  const putMakeAlbumsAvailable = useCallback(async (ids: Array<any>) => {
    const result = await client.put(
      Config.BASE_API_URL + `/add_ugc_for_cms_album`,
      ids,
      {
        withCredentials: false
      }
    );
    return result.data;
  }, []);

  const putTeardownAlbums = useCallback(async (ids: Array<any>) => {
    const result = await client.put(
      Config.BASE_API_URL + `/teardown_cms_album_and_rights`,
      ids,
      {
        withCredentials: false
      }
    );
    return result.data;
  }, []);

  const postDraftImageUpload = useCallback(
    async (albumId: number, file: File | Blob) => {
      const formData = new FormData();
      formData.append("file", file);
      formData.append("album_id", albumId + "");
      const result = await client.postFile(
        Config.BASE_API_URL + `/image_upload`,
        formData,
        {
          withCredentials: false
        }
      );

      return result.data;
    },
    []
  );

  const postUploadLyricsFile = useCallback(async (file: File | Blob) => {
    const formData = new FormData();
    formData.append("file", file);
    const result = await client.postAsPutFile(
      Config.BASE_API_URL + `/upload_lyrics_file/`,
      formData,
      {
        withCredentials: false
      }
    );

    return result.data;
  }, []);

  const postArtistCreate = useCallback(async (params: { canonical_name: string }) => {
    const result = await client.post<CreateArtistPayload>(
      Config.BASE_API_URL + `/artist_create/`,
      params,
      {
        withCredentials: false
      }
    );
    return result.data;
  }, []);

  const getCreatedArtists = useCallback(async () => {
    const result = await client.get(
      Config.BASE_API_URL + `/created_artists/`,
      {
        withCredentials: false
      }
    );
    return result.data;
  }, []);

  const getDashboardUpcomingReleases = useCallback(async (params: PartialNullable<DashboardUpcomingReleaseParams>) => {
    const result = await client.get<DashboardUpcomingReleaseResponse>(
      Config.BASE_API_URL + `/dashboard/upcoming_releases`,
      {
        params,
        withCredentials: false
      }
    );
    return result.data;
  }, []);

  const getTopTracksPrevWeek = useCallback(async (params: PartialNullable<ContractorParams>) => {
    const result = await client.get<DashboardTopEntityResponse<TopTrack>>(
      Config.BASE_STATS_API_URL + `/top/track/prev_week/`,
      {
        params,
        withCredentials: false
      }
    );
    return result.data;
  }, []);

  const getTopArtistPrevWeek = useCallback(async (params: PartialNullable<ContractorParams>) => {
    const result = await client.get<DashboardTopEntityResponse<TopArtist>>(
      Config.BASE_STATS_API_URL + `/top/artist/prev_week/`,
      {
        params,
        withCredentials: false
      }
    );
    return result.data;
  }, []);

  const getTopArtistLastWeek = useCallback(async (params: PartialNullable<ContractorParams>) => {
    const result = await client.get<DashboardTopEntityResponse<TopArtist>>(
      Config.BASE_STATS_API_URL + `/top/artist/last_week/`,
      {
        params,
        withCredentials: false
      }
    );
    return result.data;
  }, []);

  const getTopTracksLastWeek = useCallback(async (params: PartialNullable<ContractorParams>) => {
    const result = await client.get<DashboardTopEntityResponse<TopArtist>>(
      Config.BASE_STATS_API_URL + `/top/track/last_week/`,
      {
        params,
        withCredentials: false
      }
    );
    return result.data;
  }, []);


  return {
    postArtistCreate,
    getCreatedArtists,
    getAlbums,
    postCreateAlbumDraft,
    downloadStatisticsReport,
    checkPrepareDownloadStatisticsReport,
    startDownloadStatisticsReport,
    startDownloadStatisticsReportWithoutDate,
    getCanAccessStatisticsReport,
    getStatisticsDataItems,
    getStatisticsForTrack,
    getStatisticsForAlbum,
    getStatisticsTops,
    getStatisticsTracks,
    getStatisticsAlbums,
    getAlbum,
    getAlbumURL,
    getAlbumDraft,
    getTracks,
    getTrack,
    getDrafts,
    getDraftTrack,
    getCmsPayment,
    getGenres,
    getContractors,
    patchUpdateAlbumDraft,
    postUpdateMultiTrack,
    patchUpdateTrack,
    postMoveAlbumToDraft,
    postTearDownAlbum,
    postPublishAlbumDraft,
    deleteDraftAlbum,
    postUploadFile,
    putMakeAlbumsAvailable,
    putTeardownAlbums,
    putUpdateReleaseAlbumsDate,
    postDraftImageUpload,
    postUploadLyricsFile,

    getDashboardUpcomingReleases,
    getTopTracksLastWeek,
    getTopTracksPrevWeek,

    getTopArtistLastWeek,
    getTopArtistPrevWeek
  };
}
